import type {
  ChangeStakeholderPostalAddressRequestPayload,
  ChangeStakeholderVisitAddressRequestPayload,
  Stakeholder,
  GetStakeholderRequestResponse,
  ChangeStakeholderInfoRequestPayload,
  ChangeStakeholderInternalIdentificationNumbersRequestPayload,
  ChangeStakeholderBusinessNumbersRequestPayload,
  ChangeStakeholderBillingDetailsRequestPayload,
  RestoreStakeholderRequestParams,
  GetStakeholderRequestParams,
  ChangeStakeholderBillingDetailsRequestParams,
  ChangeStakeholderBusinessNumbersRequestParams,
  ChangeStakeholderInfoRequestParams,
  ChangeStakeholderInternalIdentificationNumbersRequestParams,
  ChangeStakeholderPostalAddressRequestParams,
  ChangeStakeholderVisitAddressRequestParams,
  RemoveStakeholderRequestParams
} from '@gat-ship/types'
import { useGsCloudBackend } from '@gat-ship/http-client'

export function useStakeholderApiCalls() {
  const { apiGet, apiPut, apiDelete } = useGsCloudBackend()

  /** Gets data about a stakeholder. */
  async function getStakeholderRequest(params: GetStakeholderRequestParams) {
    return await apiGet<GetStakeholderRequestResponse>(
      `stakeholders/${params.stakeholderId}`
    )
  }

  async function changeStakeholderInfoRequest(
    params: ChangeStakeholderInfoRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderInfoRequestPayload
    >(
      `/stakeholders/${params.id}/change-general-info`,
      params.payload,
      params.commandId
    )
  }

  async function changeStakeholderInternalIdentificationNumbersRequest(
    params: ChangeStakeholderInternalIdentificationNumbersRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderInternalIdentificationNumbersRequestPayload
    >(
      `stakeholders/${params.id}/change-system-reference-numbers`,
      params.payload,
      params.commandId
    )
  }

  async function changeStakeholderBusinessNumbersRequest(
    params: ChangeStakeholderBusinessNumbersRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderBusinessNumbersRequestPayload
    >(
      `stakeholders/${params.id}/change-official-business-numbers`,
      params.payload,
      params.commandId
    )
  }

  async function changeStakeholderPostalAddressRequest(
    params: ChangeStakeholderPostalAddressRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderPostalAddressRequestPayload
    >(
      `stakeholders/${params.id}/change-postal-address`,
      params.payload,
      params.commandId
    )
  }

  async function changeStakeholderVisitAddressRequest(
    params: ChangeStakeholderVisitAddressRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderVisitAddressRequestPayload
    >(
      `stakeholders/${params.id}/change-visit-address`,
      params.payload,
      params.commandId
    )
  }

  async function changeStakeholderBillingDetailsRequest(
    params: ChangeStakeholderBillingDetailsRequestParams
  ) {
    return await apiPut<
      { newState: Stakeholder },
      ChangeStakeholderBillingDetailsRequestPayload
    >(
      `stakeholders/${params.id}/change-billing-details`,
      params.payload,
      params.commandId
    )
  }

  async function removeStakeholderRequest(
    params: RemoveStakeholderRequestParams
  ) {
    return await apiDelete<{ newState: Stakeholder }, undefined>(
      `stakeholders/${params.id}`,
      undefined,
      params.commandId
    )
  }

  async function restoreStakeholderRequest(
    params: RestoreStakeholderRequestParams
  ) {
    return await apiPut<{ newState: Stakeholder }, undefined>(
      `stakeholders/${params.id}/restore`,
      undefined,
      params.commandId
    )
  }

  return {
    getStakeholderRequest,
    changeStakeholderInfoRequest,
    changeStakeholderInternalIdentificationNumbersRequest,
    changeStakeholderBusinessNumbersRequest,
    changeStakeholderPostalAddressRequest,
    changeStakeholderVisitAddressRequest,
    changeStakeholderBillingDetailsRequest,
    removeStakeholderRequest,
    restoreStakeholderRequest
  }
}
